import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { FEEDBACK_ALLOWED, FEEDBACK_MAX } from '@sh/shared'
import type { Ack } from '@sh/shared'
import { sendFeedback } from '../net/feedback.js'

/** Shown once the box is nearly full; a counter from five hundred reads as a quota. */
const COUNT_FROM = 100

/**
 * The characters in `text` that the server will not accept, deduplicated and
 * in the order they appear.
 *
 * Naming them is the whole point. The usual way to trip the ASCII rule is a
 * phone's own smart quotes and em dashes - which the person did not type, did
 * not choose, and cannot see as a category - so "letters and basic punctuation
 * only" on its own reads as a riddle. Showing the offending characters turns it
 * into a thing to delete.
 */
function offenders(text: string): string[] {
  const bad = new Set<string>()
  for (const char of text) {
    if (char !== '\n' && !FEEDBACK_ALLOWED.test(char)) bad.add(char)
  }
  return [...bad]
}

/**
 * A box to say something in, and the only channel in this application that
 * carries words rather than moves.
 *
 * It takes no view, no lobby code, no seat and no phase, and that is the
 * feature rather than an omission: what this can send is checkable by reading
 * the signature, the same way a table screen is known to be anonymous because
 * it is never handed a viewer.
 *
 * Renders its own trigger, so the three places that offer it differ by a class
 * and a label rather than by having three copies of a dialog.
 */
export function Feedback({
  children = 'Send feedback',
  className = 'home__jump',
  testId = 'feedback',
  send = sendFeedback,
}: {
  children?: ReactNode
  /** `home__jump` in a line of links; `btn btn--ghost btn--block` after a game. */
  className?: string
  testId?: string
  /** The seam the stories use. The application only ever passes the real one. */
  send?: (message: string) => Promise<Ack<null>>
}) {
  const [open, setOpen] = useState(false)
  /*
   * Held above the dialog, not inside it. A mis-tapped scrim, an Escape meant
   * for the keyboard, a POST that died on a hotel wifi - none of them may cost
   * somebody the paragraph they just wrote. HostTools can throw its state away
   * with its panel because its state is a PIN it can reissue; this is a
   * person's words, and there is nowhere else they exist.
   */
  const [text, setText] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)

  const trigger = useRef<HTMLButtonElement>(null)
  const area = useRef<HTMLTextAreaElement>(null)
  const card = useRef<HTMLDivElement>(null)
  const done = useRef<HTMLButtonElement>(null)
  const noteId = useId()

  const bad = offenders(text)
  const ready = text.trim().length > 0 && bad.length === 0 && status !== 'sending'

  const close = () => {
    setOpen(false)
    setError(null)
    // Back to a fresh box. Without this, somebody who says one thing and later
    // comes back to say another is shown the thank-you they already read, with
    // no way to type - the dialog remembering a finished conversation.
    setStatus('idle')
    // Back where they came from. Without this, Escape on the game-over screen
    // drops focus to the body and the next Tab starts from the top of the page.
    trigger.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return close()
      if (event.key !== 'Tab' || !card.current) return
      /*
       * `aria-modal` is a promise, and a dialog you can tab out the back of is
       * not keeping it. HostTools does not do this and mostly gets away with
       * it; a dialog built around a field cannot, because tabbing out lands in
       * a page a screen reader has just been told is not there.
       */
      const stops = [...card.current.querySelectorAll<HTMLElement>('button, textarea')].filter(
        (el) => !el.hasAttribute('disabled'),
      )
      const first = stops[0]
      const last = stops[stops.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Straight into the box: nobody opens this dialog to look at it.
  useEffect(() => {
    if (open && status !== 'sent') area.current?.focus()
  }, [open, status])

  // The textarea has just unmounted, so focus would otherwise fall to the body.
  useEffect(() => {
    if (status === 'sent') done.current?.focus()
  }, [status])

  const submit = async () => {
    const message = text.trim()
    if (message.length === 0 || status === 'sending') return
    setStatus('sending')
    setError(null)
    const ack = await send(message)
    if (!ack.ok) {
      setStatus('idle')
      setError(ack.error)
      area.current?.focus()
      return
    }
    setStatus('sent')
    // Cleared here and nowhere else: the one moment the words exist somewhere
    // other than this box.
    setText('')
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={className}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={`${testId}-open`}
      >
        {children}
      </button>

      {/*
        Out to the body rather than left where the trigger sits. The trigger is
        deliberately inline - a button in a row of links, a block after a game -
        and that puts the dialog inside whatever the caller styled that row
        with. The home page's old row of quiet links was a worked example: its
        `opacity: 0.75` applied to the whole subtree, so the dialog came out washed out, and the
        stacking context that opacity creates let the page paint over it. A
        dialog that covers the page cannot be a child of a paragraph on it.
      */}
      {open &&
        createPortal(
          <div className="modal" data-testid={testId}>
            <button
              type="button"
              className="modal__scrim"
              aria-label="Close feedback"
              onClick={close}
            />
            <div
              ref={card}
              className="modal__card"
              role="dialog"
              aria-modal="true"
              aria-label="Send feedback"
            >
              <header className="modal__head">
                <h2 className="card__title">Say something</h2>
                <button
                  type="button"
                  className="modal__close"
                  aria-label="Close"
                  onClick={close}
                  data-testid={`${testId}-close`}
                >
                  ✕
                </button>
              </header>

              {status === 'sent' ? (
                <>
                  {/* Announced, not just shown: the box somebody was looking at
                      has gone, and a screen reader has no other way to know. */}
                  <p className="card__hint" role="status" data-testid={`${testId}-sent`}>
                    Sent. Thank you — that is genuinely how anything here gets fixed.
                  </p>
                  <button
                    ref={done}
                    type="button"
                    className="btn btn--primary btn--block"
                    onClick={close}
                    data-testid={`${testId}-done`}
                  >
                    Done
                  </button>
                  <p className="home__hint">
                    <button
                      type="button"
                      className="home__jump"
                      onClick={() => setStatus('idle')}
                      data-testid={`${testId}-again`}
                    >
                      Say something else
                    </button>
                  </p>
                </>
              ) : (
                <form
                  className="form"
                  onSubmit={(event) => {
                    event.preventDefault()
                    if (ready) void submit()
                  }}
                >
                  {/*
                    The same thing the reveal screen does with its own limits:
                    say them, where they apply, before anybody has typed
                    something they would want back.
                  */}
                  <p className="card__hint" id={noteId} data-testid={`${testId}-note`}>
                    Your words and nothing else. Not your name, not your seat, not the lobby code,
                    not the game you just played — this box is not told any of them. Which also
                    means nothing can come back to you: if you need an answer, this is the wrong
                    shape.
                  </p>

                  <label className="field">
                    <span className="field__label">What happened?</span>
                    <textarea
                      ref={area}
                      className="field__input field__input--area"
                      value={text}
                      onChange={(event) => setText(event.target.value)}
                      maxLength={FEEDBACK_MAX}
                      rows={5}
                      aria-describedby={noteId}
                      placeholder="what worked, what got in the way, what you expected instead"
                      data-testid={`${testId}-text`}
                    />
                    {bad.length > 0 && (
                      <span className="field__hint" data-testid={`${testId}-charset`}>
                        Remove {bad.map((c) => `"${c}"`).join(' ')} — letters, numbers and basic
                        punctuation only. Curly quotes and long dashes usually come from the
                        keyboard rather than from you.
                      </span>
                    )}
                    {FEEDBACK_MAX - text.length <= COUNT_FROM && (
                      <span className="field__count" data-testid={`${testId}-count`}>
                        {FEEDBACK_MAX - text.length} characters left
                      </span>
                    )}
                  </label>

                  {error && (
                    <p className="error" role="alert" data-testid={`${testId}-error`}>
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn--primary btn--block"
                    disabled={!ready}
                    data-testid={`${testId}-send`}
                  >
                    {status === 'sending' ? 'sending…' : error ? 'Try again' : 'Send it'}
                  </button>
                </form>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
